import { supabase } from '../../config/supabase';

export interface FlightDetails {
  id: string;
  expense_id: string;
  trip_id: string;
  flight_class: string | null;
  actual_flight_date: string | null;
  departure_airport: string | null;
  arrival_airport: string | null;
  flight_number: string | null;
  destination_city: string | null;
  destination_country: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateFlightDetailsInput {
  expense_id: string;
  trip_id: string;
  flight_class?: string;
  actual_flight_date?: string | null;
  departure_airport?: string | null;
  arrival_airport?: string | null;
  flight_number?: string | null;
  destination_city?: string | null;
  destination_country?: string | null;
}

export interface UpdateFlightDetailsInput {
  flight_class?: string;
  actual_flight_date?: string | null;
  departure_airport?: string | null;
  arrival_airport?: string | null;
  flight_number?: string | null;
  destination_city?: string | null;
  destination_country?: string | null;
}

/**
 * Normalize flight details data to ensure all fields are present
 * This is necessary for backwards compatibility before migration is run
 */
const normalizeFlightDetails = (data: any): FlightDetails => {
  return {
    ...data,
    destination_city: data.destination_city ?? null,
    destination_country: data.destination_country ?? null,
  };
};

export const flightDetailsService = {
  /**
   * Get flight details by expense ID
   */
  async getByExpenseId(expenseId: string): Promise<FlightDetails | null> {
    const { data, error } = await supabase
      .from('flight_details')
      .select('*')
      .eq('expense_id', expenseId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - this is expected for expenses without flight details
        return null;
      }
      console.error('Error fetching flight details:', error);
      throw error;
    }

    return normalizeFlightDetails(data);
  },

  /**
   * Get all flight details for a trip
   */
  async getByTripId(tripId: string): Promise<FlightDetails[]> {
    const { data, error } = await supabase
      .from('flight_details')
      .select('*')
      .eq('trip_id', tripId)
      .order('actual_flight_date', { ascending: true, nullsFirst: false });

    if (error) {
      console.error('Error fetching flight details for trip:', error);
      throw error;
    }

    return (data || []).map(normalizeFlightDetails);
  },

  /**
   * Create flight details
   */
  async create(input: CreateFlightDetailsInput): Promise<FlightDetails> {
    const { data, error } = await supabase
      .from('flight_details')
      .insert({
        expense_id: input.expense_id,
        trip_id: input.trip_id,
        flight_class: input.flight_class || 'Economy',
        actual_flight_date: input.actual_flight_date,
        departure_airport: input.departure_airport,
        arrival_airport: input.arrival_airport,
        flight_number: input.flight_number,
        destination_city: input.destination_city,
        destination_country: input.destination_country,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating flight details:', error);
      throw error;
    }

    return normalizeFlightDetails(data);
  },

  /**
   * Update flight details by expense ID
   */
  async updateByExpenseId(
    expenseId: string,
    input: UpdateFlightDetailsInput
  ): Promise<FlightDetails> {
    const { data, error } = await supabase
      .from('flight_details')
      .update(input)
      .eq('expense_id', expenseId)
      .select()
      .single();

    if (error) {
      console.error('Error updating flight details:', error);
      throw error;
    }

    return normalizeFlightDetails(data);
  },

  /**
   * Upsert flight details - create if doesn't exist, update if it does
   */
  async upsert(input: CreateFlightDetailsInput): Promise<FlightDetails> {
    const { data, error } = await supabase
      .from('flight_details')
      .upsert(
        {
          expense_id: input.expense_id,
          trip_id: input.trip_id,
          flight_class: input.flight_class || 'Economy',
          actual_flight_date: input.actual_flight_date,
          departure_airport: input.departure_airport,
          arrival_airport: input.arrival_airport,
          flight_number: input.flight_number,
          destination_city: input.destination_city,
          destination_country: input.destination_country,
        },
        {
          onConflict: 'expense_id',
        }
      )
      .select()
      .single();

    if (error) {
      console.error('Error upserting flight details:', error);
      throw error;
    }

    return normalizeFlightDetails(data);
  },

  /**
   * Delete flight details by expense ID
   */
  async deleteByExpenseId(expenseId: string): Promise<void> {
    const { error } = await supabase
      .from('flight_details')
      .delete()
      .eq('expense_id', expenseId);

    if (error) {
      console.error('Error deleting flight details:', error);
      throw error;
    }
  },
};
